import JSZip from "jszip";
import { saveAs } from "file-saver";
import { createTableLayoutPdf } from "./exportTableLayoutPdf";

// ---------------------------------------------------
// UTIL
// ---------------------------------------------------

function sanitize(str) {
  if (!str) return "file";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function colorLabel(table) {
  const name = table.color?.name ?? "szin-nelkul";
  const milling = table.milling?.name?.trim();
  if (!milling) return name;
  const hyphenIndex = name.indexOf("-");
  const index = hyphenIndex >= 0 ? hyphenIndex : name.indexOf(" ");
  if (index < 0) return `${name} ${milling}`;
  return `${name.slice(0, index).trimEnd()} ${milling} ${name.slice(index).trimStart()}`;
}

function partyName(work) {
  return [work?.username ?? "user", work?.clientName ?? work?.client?.name]
    .filter(Boolean).map(sanitize).join("_");
}

function isHalfTable(table) {
  return table?.split === true || table?.isHalfTable === true;
}

function groupKey(table) {
  const colorId = table?.color?.colorId;
  const millingId = table?.milling?.millingId;
  return JSON.stringify([
    colorId == null ? null : String(colorId),
    millingId == null ? null : String(millingId)
  ]);
}

function groupSuffix(table) {
  const encode = (value) =>
    value == null
      ? "NONE"
      : Array.from(String(value), (character) =>
          character.codePointAt(0).toString(16).padStart(6, "0")
        ).join("");

  return `C${encode(table?.color?.colorId)}_M${encode(table?.milling?.millingId)}`;
}

function buildTableFile(work, table) {
  let out = "";
  out += table.dati?.replace(/\n/g, "\r\n") ?? "";
  out += "\r\n";
  out += table.righe?.replace(/\n/g, "\r\n") ?? "";
  out += table.riferimenti?.replace(/\n/g, "\r\n") ?? "";
  return out;
}

function buildMasterFile(refs) {
  let out = "[Intestazione]\r\n";
  out += "Descrizione=\r\n\r\n";
  out += "[Righe]\r\n";
  out += `NumeroRighe=${refs.length}\r\n`;

  refs.forEach((r, i) => {
    out += `${i + 1}=${r}\r\n`;
  });

  return out;
}

// ---------------------------------------------------
// CORE EXPORT
// ---------------------------------------------------

async function buildZip(work, tables, items = []) {
  const zip = new JSZip();

  const userName = partyName(work);
  const workId = sanitize(work?.workId ?? "0");

  const rootDir = `${userName}_${workId}/`;
  const listaDir = `${rootDir}lista/`;
  const programDir = `${rootDir}program/`;
  const pdfDir = `${rootDir}pdf/`;

  zip.folder(rootDir);
  zip.folder(listaDir);
  zip.folder(programDir);
  zip.folder(pdfDir);

  const tablesByGroup = new Map();

  tables.forEach((t) => {
    const key = groupKey(t);
    if (!tablesByGroup.has(key)) {
      tablesByGroup.set(key, { label: colorLabel(t), tables: [] });
    }
    tablesByGroup.get(key).tables.push(t);
  });

  const groups = [...tablesByGroup.values()];
  const baseStems = groups.map(({ label, tables: groupTables }) => {
    const tableCount = groupTables.reduce(
      (sum, table) => sum + (isHalfTable(table) ? 0.5 : 1),
      0
    );
    return `${userName}_${sanitize(label)}_${workId}_${tableCount}T`;
  });
  const stemCounts = new Map();
  baseStems.forEach((stem) => {
    const key = stem.toLocaleLowerCase("en-US");
    stemCounts.set(key, (stemCounts.get(key) ?? 0) + 1);
  });

  // ---- SZÍN + MARÁS CSOPORTONKÉNT
  for (const [groupIndex, group] of groups.entries()) {
    const { label, tables: colorTables } = group;
    const tableCount = colorTables.reduce(
      (sum, table) => sum + (isHalfTable(table) ? 0.5 : 1),
      0
    );
    const duplicateStem =
      stemCounts.get(baseStems[groupIndex].toLocaleLowerCase("en-US")) > 1;
    const identitySuffix = duplicateStem
      ? `_${groupSuffix(colorTables[0])}`
      : "";
    const groupStem =
      `${userName}_${sanitize(label)}${identitySuffix}_${workId}_${tableCount}T`;
    const refs = [];

    colorTables.forEach((tbl, index) => {
      const base = `${groupStem}_${index + 1}`;

      const filePath = `${programDir}${base}.000`;

      const content = buildTableFile(work, tbl);
      zip.file(filePath, content);

      refs.push(`C:\\WinCut\\Prg\\${base}.000,1,0`);
    });

    // ---- MASTER FILE
    const masterName = `${listaDir}${groupStem}.000`;

    const masterContent = buildMasterFile(refs);
    zip.file(masterName, masterContent);

    const layoutPdf = await createTableLayoutPdf({
      work,
      tables: colorTables,
      items,
      tableCount
    });
    zip.file(`${pdfDir}${groupStem}.pdf`, layoutPdf);
  }

  return zip;
}

// ---------------------------------------------------
// PUBLIC API
// ---------------------------------------------------

export async function exportByWork(work, createdTables, items = []) {
  const zip = await buildZip(work, createdTables, items);
  const blob = await zip.generateAsync({ type: "blob" });

  const userName = partyName(work);
  const workId = sanitize(work?.workId);

  saveAs(blob, `work-${userName}_${workId}.zip`);
}

export async function exportByColor(work, createdTables, colorId, items = []) {
  const ids = new Set((Array.isArray(colorId) ? colorId : [colorId]).map(String));
  const filtered = createdTables.filter(
    (t) => ids.has(String(t.color?.colorId))
  );

  const zip = await buildZip(work, filtered, items);
  const blob = await zip.generateAsync({ type: "blob" });

  const userName = partyName(work);
  const workId = sanitize(work?.workId);

  const labels = [...new Set(filtered.map(colorLabel))].map(sanitize).join("_");
  saveAs(blob, `work-${userName}_${labels}_${workId}.zip`);
}

export async function exportSeparately(work, createdTables, items = []) {
  const userName = partyName(work);
  const workId = sanitize(work?.workId);

  for (const table of createdTables) {
    const zip = await buildZip(work, [table], items);
    const blob = await zip.generateAsync({ type: "blob" });

    saveAs(blob, `work-${userName}_${sanitize(colorLabel(table))}_${workId}_T${table.id}.zip`);
  }
}
